import { fetchApi } from '@vend/utilities'
import { ApiTransaction } from '@vend/types'
import {
  LSPAY_PRODUCT_VERSION,
  LSPAY_TRANSACTION_STATUS,
} from './lspayConstants'
import {
  LSPAYCreateTransactionOptions,
  LSPAYCancelTransactionOptions,
  LSPAYGetTransactionOptions,
  LSPAYRetryTransactionOptions,
  SwiperErrorResponse,
  SwiperError,
} from './lspay.types'
import { withRetry } from './withRetry'

interface BaseTransaction {
  register_sale_payment_id: string
  payment_intent_id: string
  reason: string
}

// Define a type for completed transactions
export interface TransactionCompleted extends BaseTransaction {
  status: LSPAY_TRANSACTION_STATUS.COMPLETED
  transaction: ApiTransaction
}

export interface TransactionNotCompleted extends BaseTransaction {
  status: Exclude<LSPAY_TRANSACTION_STATUS, LSPAY_TRANSACTION_STATUS.COMPLETED> // Exclude 'COMPLETED' status
}

export type CreateTransactionResponse =
  | TransactionCompleted
  | TransactionNotCompleted

export type CancelTransactionResponse =
  | TransactionCompleted
  | TransactionNotCompleted

export type LSPAYRetryTransactionResponse =
  | TransactionCompleted
  | TransactionNotCompleted

const DEFAULT_POLLING_TIMEOUT = 2000 // 2 seconds

// helper functions specific to LSD (LSPay server-driven)

// create a transaction
export const createTransaction = async ({
  amount,
  currency,
  registerId,
  retailerPaymentTypeId,
  registerSalePaymentId,
  withSurcharge,
  tipEligibleAmount,
  metadata,
}: LSPAYCreateTransactionOptions): Promise<CreateTransactionResponse> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/transactions`,
    {
      method: 'POST',
      body: JSON.stringify({
        amount,
        currency,
        register_id: registerId,
        retailer_payment_type_id: retailerPaymentTypeId,
        register_sale_payment_id: registerSalePaymentId,
        with_surcharge: withSurcharge,
        tip_eligible_amount: tipEligibleAmount,
        metadata: {
          ...metadata,
          product_version: LSPAY_PRODUCT_VERSION,
        },
      }),
    }
  )

  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }
  return response.json()
}

// Get the transaction status

export type LSPAYGetTransactionResponse =
  | TransactionCompleted
  | TransactionNotCompleted

export const getTransaction = async ({
  registerId,
  paymentIntentId,
  registerSalePaymentId,
}: LSPAYGetTransactionOptions): Promise<LSPAYGetTransactionResponse> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/transactions/${registerSalePaymentId}`,
    {
      method: 'POST',
      body: JSON.stringify({
        payment_intent_id: paymentIntentId,
      }),
    }
  )

  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }
  return response.json()
}

// Get the capture  transaction
export type LSPAYCaptureTransactionOptions = {
  registerId: string
  paymentIntentId: string
  registerSalePaymentId: string
}

export const captureTransaction = async ({
  registerId,
  paymentIntentId,
  registerSalePaymentId,
}: LSPAYCaptureTransactionOptions): Promise<TransactionCompleted> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/transactions/${registerSalePaymentId}/capture`,
    {
      method: 'POST',
      body: JSON.stringify({
        payment_intent_id: paymentIntentId,
      }),
    }
  )

  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }
  return response.json()
}

// cancel a transaction
export const cancelTransaction = async ({
  registerId,
  registerSalePaymentId,
  paymentIntentId,
}: LSPAYCancelTransactionOptions): Promise<CancelTransactionResponse> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/transactions/${registerSalePaymentId}/cancel_process`,
    {
      method: 'POST',
      body: JSON.stringify({ payment_intent_id: paymentIntentId }),
    }
  )
  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }
  return response.json()
}

// reprocess a transaction
export const reprocessTransaction = async ({
  currency,
  registerId,
  registerSalePaymentId,
  paymentIntentId,
  withSurcharge,
  tipEligibleAmount,
}: LSPAYRetryTransactionOptions): Promise<LSPAYRetryTransactionResponse> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/transactions/${registerSalePaymentId}/reprocess`,
    {
      method: 'POST',
      body: JSON.stringify({
        currency,
        payment_intent_id: paymentIntentId,
        with_surcharge: withSurcharge,
        tip_eligible_amount: tipEligibleAmount,
      }),
    }
  )
  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }
  return response.json()
}

// Capture a transaction with retries
export const captureTransactionWithRetry = async ({
  registerId,
  paymentIntentId,
  registerSalePaymentId,
}: LSPAYCaptureTransactionOptions): Promise<TransactionCompleted> => {
  const captureResult = await Promise.all([
    withRetry<TransactionCompleted>(
      () =>
        captureTransaction({
          registerId,
          paymentIntentId,
          registerSalePaymentId,
        }),
      'captureTransaction(LSD)'
    ),
  ])
  return captureResult[0]
}

// Gets the timeout to wait for a polling
export const getPollingTimeout = (): number => {
  return DEFAULT_POLLING_TIMEOUT
}

// Custom type guard function to check if the transaction is completed
export const isTransactionCompleted = (
  data: TransactionCompleted | TransactionNotCompleted
): data is TransactionCompleted => {
  return data.status === LSPAY_TRANSACTION_STATUS.COMPLETED
}
